// Route configuration for the application

export const routeConfig = {
  // Public routes
  login: '/login',

  // Test route for backend integration
  testIntegration: '/test-integration',

  // Dashboard routes
  dashboard: '/dashboard',

  // Common routes (available to all roles)
  tasks: '/tasks',
  taskDetail: '/tasks/:taskId',
  editTask: '/tasks/:taskId/edit',
  labs: '/labs',
  notifications: '/notifications',
  feedback: '/feedback',
  messages: '/messages',
  profile: '/profile',

  // Joiner-specific routes
  progress: '/progress',
  roadmap: '/roadmap',
  taskReport: '/task-report',
  achievements: '/achievements',
  submitDocuments: '/submit-documents',

  // Buddy-specific routes
  joiners: '/joiners',
  joinerTasks: '/joiners/:joinerId/tasks',
  reviews: '/reviews',
  buddyReviewDetail: '/reviews/:reviewId',
  joinerReviewDetail: '/joiners/reviews/:reviewId',

  // HR-specific routes
  analytics: '/analytics',
  certificates: '/certificates',
  teamManagement: '/team-management',
  teamsHRMS: '/teams-hrms',
  teamDetails: '/team-details/:teamId',
  hrReviews: '/hr-reviews',
  hrReviewDetail: '/hr-reviews/:reviewId',
  scheduleDemo: '/schedule-demo',
  preOnboarding: '/pre-onboarding',
  verification: '/verification',
  verificationDetail: '/verification/:joinerEmail',

  // Admin-specific routes
  adminReviews: '/admin-reviews',
  adminReviewDetail: '/admin-reviews/:reviewId',

  // Manager-specific routes
  managerReviews: '/manager-reviews',
  managerReviewDetail: '/manager-reviews/:reviewId',

  // Team Lead-specific routes
  myTeam: '/my-team',
  teamLeadReviews: '/team-lead-reviews',
  teamLeadReviewDetail: '/team-lead-reviews/:reviewId',

  // Full-time performance track
  performance: '/performance',
  performanceMeetings: '/performance-meetings',
  performanceMeetingDetail: '/performance-meetings/:meetingId',
  performanceQuarterly: '/performance-quarterly',
  performanceGoals: '/performance-goals',
  performanceFeedback: '/performance-feedback',

  // Shared HR/Buddy routes
  overdueTasks: '/overdue-tasks',
};

export const getRouteFromTab = (tab: string, userRole?: string): string => {
  switch (tab) {
    case 'dashboard': return routeConfig.dashboard;
    case 'tasks': return routeConfig.tasks;
    case 'content': return routeConfig.labs;
    case 'learning-hub': return routeConfig.labs;
    case 'labs': return routeConfig.labs;
    case 'progress': return routeConfig.progress;
    case 'roadmap': return routeConfig.roadmap;
    case 'task-report': return routeConfig.taskReport;
    case 'achievements': return routeConfig.achievements;
    case 'submit-documents': return routeConfig.submitDocuments;
    case 'notifications': return routeConfig.notifications;
    case 'feedback': return routeConfig.feedback;
    case 'messages': return routeConfig.messages;
    case 'profile': return routeConfig.profile;
    case 'joiners': return routeConfig.joiners;
    case 'reviews':
      // Route to role-specific reviews
      if (userRole?.toLowerCase() === 'admin') return routeConfig.adminReviews;
      if (userRole?.toLowerCase() === 'hr') return routeConfig.hrReviews;
      if (userRole?.toLowerCase() === 'manager') return routeConfig.managerReviews;
      if (userRole === 'buddy') return routeConfig.reviews;
      return routeConfig.reviews;
    case 'admin-reviews': return routeConfig.adminReviews;
    case 'hr-reviews': return routeConfig.hrReviews;
    case 'schedule-demo': return routeConfig.scheduleDemo;
    case 'manager-reviews': return routeConfig.managerReviews;
    case 'analytics': return routeConfig.analytics;
    case 'certificates': return routeConfig.certificates;
    case 'team-management': return routeConfig.teamManagement;
    case 'teams-hrms': return routeConfig.teamsHRMS;
    case 'my-team': return routeConfig.myTeam;
    case 'team-lead-reviews': return routeConfig.teamLeadReviews;
    case 'overdue-tasks': return routeConfig.overdueTasks;
    case 'pre-onboarding': return routeConfig.preOnboarding;
    case 'verification': return routeConfig.verification;
    case 'performance': return routeConfig.performance;
    case 'performance-meetings': return routeConfig.performanceQuarterly;
    case 'performance-quarterly': return routeConfig.performanceQuarterly;
    case 'performance-goals': return routeConfig.performanceGoals;
    case 'performance-feedback': return routeConfig.performanceFeedback;
    default: return routeConfig.dashboard;
  }
};

export const getTabFromRoute = (path: string): string => {
  // Handle dynamic routes with parameters
  if (path.startsWith('/team-details/')) {
    return 'team-management';
  }
  if (path.startsWith('/joiners/')) {
    return 'joiners';
  }
  if (path.startsWith('/reviews/')) {
    return 'reviews';
  }
  if (path.startsWith('/hr-reviews/')) {
    return 'hr-reviews';
  }
  if (path.startsWith('/manager-reviews/')) {
    return 'manager-reviews';
  }
  if (path.startsWith('/admin-reviews/')) {
    return 'admin-reviews';
  }
  if (path.startsWith('/team-lead-reviews/')) {
    return 'team-lead-reviews';
  }
  if (path.startsWith('/tasks/')) {
    return 'tasks';
  }
  if (path.startsWith('/verification/')) {
    return 'verification';
  }

  if (path.startsWith('/performance-meetings/')) {
    return 'performance-quarterly';
  }
  if (path.startsWith('/performance-quarterly')) {
    return 'performance-quarterly';
  }

  switch (path) {
    case routeConfig.dashboard: return 'dashboard';
    case routeConfig.tasks: return 'tasks';
    case routeConfig.labs: return 'labs';
    case routeConfig.progress: return 'progress';
    case routeConfig.roadmap: return 'roadmap';
    case routeConfig.taskReport: return 'task-report';
    case routeConfig.achievements: return 'achievements';
    case routeConfig.submitDocuments: return 'submit-documents';
    case routeConfig.notifications: return 'notifications';
    case routeConfig.feedback: return 'feedback';
    case routeConfig.messages: return 'messages';
    case routeConfig.profile: return 'profile';
    case routeConfig.joiners: return 'joiners';
    case routeConfig.reviews: return 'reviews';
    case routeConfig.analytics: return 'analytics';
    case routeConfig.certificates: return 'certificates';
    case routeConfig.teamManagement: return 'team-management';
    case routeConfig.teamsHRMS: return 'teams-hrms';
    case routeConfig.hrReviews: return 'hr-reviews';
    case routeConfig.scheduleDemo: return 'schedule-demo';
    case routeConfig.managerReviews: return 'manager-reviews';
    case routeConfig.adminReviews: return 'admin-reviews';
    case routeConfig.myTeam: return 'my-team';
    case routeConfig.teamLeadReviews: return 'team-lead-reviews';
    case routeConfig.overdueTasks: return 'overdue-tasks';
    case routeConfig.preOnboarding: return 'pre-onboarding';
    case routeConfig.verification: return 'verification';
    case routeConfig.performance: return 'performance';
    case routeConfig.performanceMeetings: return 'performance-quarterly';
    case routeConfig.performanceQuarterly: return 'performance-quarterly';
    case routeConfig.performanceGoals: return 'performance-goals';
    case routeConfig.performanceFeedback: return 'performance-feedback';
    default: return 'dashboard';
  }
};
